import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { normalizeVendorDashboard } from '@/lib/normalize';
import { formatHours, formatPercent, orDash } from '@/lib/format';
import { PageHeader } from '@/ui/page-header';
import { Panel } from '@/ui/panel';
import { StatTile } from '@/ui/stat-tile';
import { ActivityList } from '@/domain/activity-list';
import { ActionList } from '@/domain/action-list';
import { useProfile } from '@/app/session';
import { ErrorState } from '@/ui/error-state';
import { useRealtimeRefresh } from '@/lib/realtime';

export function VendorHome() {
  const { data: profile } = useProfile('vendor');
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['vendor', 'dashboard'],
    queryFn: async () => normalizeVendorDashboard(await api('/dashboard/stats'))
  });

  useRealtimeRefresh('/datasets/subscription-token', [['vendor', 'dashboard']], { realm: 'vendor' });

  const org = profile?.organization_name ?? profile?.name ?? 'there';
  const inEval = data?.in_evaluation;
  const flags = data?.open_flags;
  const passRate = data?.pass_rate_90d;

  const subtitle = [
    inEval ? `${inEval.count} batches in evaluation` : null,
    flags ? `${flags.count} open flags` : null,
    data?.actions.length ? null : null
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="grid gap-8">
      <PageHeader title={`Welcome back, ${org}`} subtitle={subtitle || 'No open work'} />

      {isError ? <ErrorState error={error} onRetry={() => void refetch()} title="Could not load your summary" /> : null}

      <div className="grid grid-cols-4 gap-5 max-xl:grid-cols-2 max-sm:grid-cols-1">
        <StatTile
          label="Delivered total"
          value={isPending ? '—' : formatHours(data?.delivered_hours ?? null)}
          foot={data?.certified_dataset_count ? `${data.certified_dataset_count} datasets certified` : undefined}
          footTone="pass"
        />
        <StatTile label="In evaluation" value={orDash(inEval?.count)} foot={inEval?.dataset_names.join(' · ')} />
        <StatTile
          label="Pass rate · 90d"
          value={passRate ? formatPercent(passRate.value) : '—'}
          foot={
            passRate
              ? passRate.trend_points === null
                ? `${passRate.run_count} runs`
                : `${passRate.trend_points >= 0 ? '+' : ''}${passRate.trend_points} pts vs prior 90d`
              : undefined
          }
          footTone={passRate && passRate.trend_points !== null && passRate.trend_points < 0 ? 'danger' : 'pass'}
        />
        <StatTile
          label="Open flags"
          value={orDash(flags?.count)}
          foot={flags?.blocking ? `${flags.blocking} blocking resubmission` : undefined}
          footTone="warn"
        />
      </div>

      <div className="grid grid-cols-[1fr_360px] items-start gap-5 max-xl:grid-cols-1">
        <Panel title="Recent activity">
          <ActivityList entries={data?.activity ?? []} />
        </Panel>
        <Panel title="Needs your action" accent>
          <ActionList items={data?.actions ?? []} />
        </Panel>
      </div>
    </div>
  );
}
